<template>
  <textarea
    :value="modelValue"
    @input="onInput"
    class="form-textarea resize-none border-none focus:outline-none focus:ring-0"
    :class="[
      isScrollable ? 'overflow-y-auto' : 'overflow-hidden',
      classes
    ]"
    :style="{
      minHeight: minHeight + 'px',
      maxHeight: maxHeight + 'px',
      paddingTop: '0',
      paddingBottom: '0',
      boxSizing: 'border-box',
      
      ...style,
    }"
    :placeholder="placeholder"
    @keydown.enter.exact.prevent="$emit('enter')"
    ref="textareaRef"
    rows="1"
  ></textarea>
</template>

<script setup>
import { ref, watch, onMounted, computed, nextTick } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  minHeight: {
    type: Number,
    default: 35
  },
  maxHeight: {
    type: Number,
    default: 160
  },
  placeholder: {
    type: String,
    default: ''
  },
  classes: {
    type: String,
    default: 'w-full p-1 pl-2 pr-2 border border-gpx_gray-800 rounded-lg'
  },
  style: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'enter']);
const textareaRef = ref(null);
const isScrollable = ref(false);

/**
 * Input handler
 * Updates model value and automatically resizes the textarea
 */
const onInput = (event) => {
  emit('update:modelValue', event.target.value);
  autoResizeTextarea();
};

/**
 * Automatically adjusts the height of textarea based on content
 * Sets height based on content but not exceeding maximum height
 * Enables scrolling only when maximum height is reached
 */
const autoResizeTextarea = () => {
  const textarea = textareaRef.value;
  if (!textarea) return;

  // Reset height to measure actual content height
  textarea.style.height = 'auto';

  // Check if scrolling is needed (content exceeds max height)
  const scrollHeight = textarea.scrollHeight;
  
  // Set flag to enable/disable scrolling
  isScrollable.value = scrollHeight > props.maxHeight;
  
  // Set height (not exceeding max height)
  textarea.style.height = `${Math.min(scrollHeight, props.maxHeight)}px`;
};

// Watch for external value changes
watch(() => props.modelValue, () => {
  // Use nextTick to ensure Vue has updated the DOM before resizing
  nextTick(autoResizeTextarea);
});

onMounted(() => {
  if (props.modelValue) {
    nextTick(autoResizeTextarea);
  }
});
</script>

<style scoped>
textarea {
  outline: none;
}
</style> 